import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

//const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4005";
const API_URL = process.env.REACT_APP_API_URL;
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

// Acciones asíncronas
export const fetchEpisodesBySeasonId = createAsyncThunk(
  'episodes/fetchBySeasonId', 
  async (seasonId, thunkAPI) => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/episodes/season/${seasonId}`);
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const addEpisode = createAsyncThunk(
  'episodes/addEpisode', 
  async ({ seasonId, episodeData }, thunkAPI) => {
    try {
      const authHeader = getAuthHeader();
      const response = await axios.post(
        `${API_URL}/api/v1/episodes`, 
        { ...episodeData, seasonId }, 
        authHeader
      );
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const bulkAddEpisodes = createAsyncThunk(
  'episodes/bulkAddEpisodes', 
  async ({ seasonId, episodesData }, thunkAPI) => {
    try {
      const authHeader = getAuthHeader();
      const episodesWithSeason = episodesData.map(episode => ({
        ...episode,
        seasonId: parseInt(seasonId)
      }));
      
      const response = await axios.post(`${API_URL}/api/v1/episodes/bulk`, { episodes: episodesWithSeason }, authHeader);
      return response.data.data || episodesWithSeason; // Fallback si la API no retorna los datos
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const updateEpisode = createAsyncThunk(
  'episodes/updateEpisode', 
  async ({ id, episodeData }, thunkAPI) => {
    try {
      const authHeader = getAuthHeader();
      const response = await axios.put(
        `${API_URL}/api/v1/episodes/${id}`, 
        episodeData, 
        authHeader
      );
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const deleteEpisode = createAsyncThunk(
  'episodes/deleteEpisode', 
  async (id, thunkAPI) => {
    try {
      const authHeader = getAuthHeader();
      await axios.delete(`${API_URL}/api/v1/episodes/${id}`, authHeader);
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const addEpisodeToSeason = createAsyncThunk(
  'episodes/addEpisodeToSeason', 
  async ({ animeId, seasonNumber, episodeData }, thunkAPI) => {
    try {
      const authHeader = getAuthHeader();
      const response = await axios.post(
        `${API_URL}/api/v1/animes/${animeId}/seasons/${seasonNumber}/episodes`, 
        episodeData, 
        authHeader
      );
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: 'Error al agregar el episodio' });
    }
  }
);

// Slice
const episodeSlice = createSlice({
  name: 'episodes',
  initialState: {
    items: [],
    status: 'idle',
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Manejar fetchEpisodesBySeasonId
      .addCase(fetchEpisodesBySeasonId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchEpisodesBySeasonId.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchEpisodesBySeasonId.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // Manejar addEpisode
      .addCase(addEpisode.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      // Manejar addEpisodeToSeason
      .addCase(addEpisodeToSeason.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(addEpisodeToSeason.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items.push(action.payload);
      })
      .addCase(addEpisodeToSeason.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message || action.error.message;
      })
      // Manejar bulkAddEpisodes
      .addCase(bulkAddEpisodes.fulfilled, (state, action) => {
        state.items = [...state.items, ...action.payload];
      })
      // Manejar updateEpisode
      .addCase(updateEpisode.fulfilled, (state, action) => {
        const index = state.items.findIndex(episode => episode.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      // Manejar deleteEpisode
      .addCase(deleteEpisode.fulfilled, (state, action) => {
        state.items = state.items.filter(episode => episode.id !== action.payload);
      });
  }
});

export default episodeSlice.reducer;
